import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { TerminalType } from '@/shared/types/userTerminalTypes';
import { calendarPrevYm } from '@/domains/global-dashboard/utils/chart-utils';
import { TERMINAL_TYPE_STACK, terminalMonthsByYmFromOverview, type TerminalMonthSnap } from '@/domains/global-dashboard/sections/terminals/terminal-utils';

/** Past 90 days ≈ three months of deployment history for averaging. */
export const INVENTORY_FORECAST_USAGE_MONTHS = 3;

/** Flag when stock covers fewer than ~60 days at current burn rate. */
export const INVENTORY_FORECAST_THRESHOLD_MONTHS = 2;

export const INVENTORY_FORECAST_DAYS_PER_MONTH = 30;

export type InventoryForecastSpec = {
  type: TerminalType;
  avgMonthlyNewDeployments: number | null;
  currentOnHand: number;
  monthsOfStockRemaining: number | '-'; // '-' means no data can not be calculated
  targetStock: number;
};

export type OrganizationInventoryForecast = {
  organizationId: string;
  name: string;
  specs: InventoryForecastSpec[];
};

/** Average deployment deltas for month-1, month-2, and month-3 relative to `currentYm`. */
function avgMonthlyNewDeploymentsFromPriorThreeMonths(
  months: Map<string, TerminalMonthSnap>,
  currentYm: string,
  type: TerminalType,
): number {
  const month1 = calendarPrevYm(currentYm);
  const month2 = calendarPrevYm(month1);

  const curSnap = months.get(currentYm);
  const prevSnap = months.get(month2);
  const netNew = (curSnap?.typeSlots.find((slot) => slot.type === type)?.deployed ?? 0) - (prevSnap?.typeSlots.find((slot) => slot.type === type)?.deployed ?? 0);
  return netNew > 0 ? netNew / 3 : 0;
}

/**
 * Rows flagged when `current_on_hand / avg_monthly_usage` is below the 60-day threshold.
 * `terminalsNeed` = units required to reach two months of cover at the observed burn rate.
 */
export function buildInventoryForecastRows(organizations: OrganizationOverviewData[]): OrganizationInventoryForecast[] {
  const organizationsForecast: OrganizationInventoryForecast[] = [];

  for (const organization of organizations) {
    const months = terminalMonthsByYmFromOverview(organization);
    if (months.size === 0) continue;

    const currentYm = [...months.keys()].sort((a, b) => a.localeCompare(b)).at(-1)!;
    const currentSnap = months.get(currentYm);
    if (!currentSnap) continue;

    const specs: InventoryForecastSpec[] = [];
    for (const spec of TERMINAL_TYPE_STACK) {
      const avgMonthlyNewDeployments = avgMonthlyNewDeploymentsFromPriorThreeMonths(months, currentYm, spec.type) ?? 0;
      const currentOnHand = currentSnap.typeSlots.find((slot) => slot.type === spec.type)?.standby ?? 0;
      specs.push({
        type: spec.type,
        avgMonthlyNewDeployments,
        currentOnHand: currentSnap.typeSlots.find((slot) => slot.type === spec.type)?.standby ?? 0,
        monthsOfStockRemaining: avgMonthlyNewDeployments > 0 ? Math.round(currentOnHand / avgMonthlyNewDeployments) : '-',
        targetStock: Math.ceil(INVENTORY_FORECAST_THRESHOLD_MONTHS * avgMonthlyNewDeployments),
      });
    }
    organizationsForecast.push({
      organizationId: organization.organizationId,
      name: organization.name,
      specs,
    });
  }

  return organizationsForecast;
}
